import React from 'react';
import Icon from '../../../components/AppIcon';

const STAGES = [
  {
    id: 'submitted',
    step: 1,
    label: 'Submitted',
    icon: 'Send',
    description: 'Application received and queued for review',
    estimatedTime: 'Immediate',
  },
  {
    id: 'under_review',
    step: 2,
    label: 'Under Review',
    icon: 'Search',
    description: 'Our team is reviewing your documents and information',
    estimatedTime: '1–2 business days',
  },
  {
    id: 'eligibility',
    step: 3,
    label: 'Eligibility Check',
    icon: 'ShieldCheck',
    description: 'Identity verification and SocialCents Score assessment',
    estimatedTime: '2–3 business days',
  },
  {
    id: 'decision',
    step: 4,
    label: 'Decision',
    icon: 'Sparkles',
    description: 'Final approval or conditional offer issued',
    estimatedTime: '3–5 business days',
  },
];

const STATUS_TO_STEP = {
  pending: 1,
  submitted: 1,
  under_review: 2,
  eligibility: 3,
  approved: 4,
  rejected: 4,
  decision: 4,
};

const ApplicationTimeline = ({ application }) => {
  const status = application?.status || 'pending';
  const currentStep = STATUS_TO_STEP?.[status] || 1;
  const isDecisionMade = status === 'approved' || status === 'rejected';

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr)?.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return null; }
  };

  const getStageTimestamp = (stageStep) => {
    if (stageStep === 1) return formatDate(application?.created_at);
    if (stageStep === 4 && isDecisionMade) return formatDate(application?.updated_at);
    if (stageStep < currentStep) return formatDate(application?.updated_at);
    return null;
  };

  return (
    <div
      className="rounded-2xl p-6"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <div className="flex items-center gap-2 mb-6">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)' }}
        >
          <Icon name="GitBranch" size={13} color="#fff" />
        </div>
        <h2 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Application Progress</h2>
      </div>
      <div className="space-y-0">
        {STAGES?.map((stage, idx) => {
          const isCompleted = currentStep > stage?.step || (isDecisionMade && stage?.step === 4);
          const isActive = currentStep === stage?.step && !(isDecisionMade && stage?.step === 4);
          const isPending = currentStep < stage?.step;
          const timestamp = getStageTimestamp(stage?.step);

          return (
            <div key={stage?.id} className="flex gap-4">
              {/* Icon + connector */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="relative">
                  {isActive && (
                    <div
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{ background: 'rgba(20,184,166,0.3)', animationDuration: '1.5s' }}
                    />
                  )}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center relative z-10 transition-all duration-500"
                    style={{
                      background: isCompleted
                        ? 'linear-gradient(135deg,#0d9488,#1d4ed8)'
                        : isActive
                        ? 'rgba(20,184,166,0.15)'
                        : 'rgba(255,255,255,0.04)',
                      border: isActive
                        ? '2px solid #14B8A6'
                        : isCompleted
                        ? 'none' :'2px solid rgba(255,255,255,0.1)',
                      boxShadow: isActive ? '0 0 18px rgba(20,184,166,0.35)' : 'none',
                    }}
                  >
                    <Icon
                      name={isCompleted ? 'Check' : stage?.icon}
                      size={16}
                      color={isCompleted ? '#fff' : isActive ? '#14B8A6' : 'rgba(255,255,255,0.2)'}
                    />
                  </div>
                </div>
                {idx < STAGES?.length - 1 && (
                  <div
                    className="w-px flex-1 my-1 transition-all duration-700"
                    style={{
                      background: isCompleted
                        ? 'linear-gradient(180deg,#0d9488,#1d4ed8)'
                        : 'rgba(255,255,255,0.07)',
                      minHeight: '36px',
                    }}
                  />
                )}
              </div>
              {/* Content */}
              <div className="pb-7 flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p
                      className="text-sm font-semibold transition-colors"
                      style={{
                        color: isActive ? '#14B8A6' : isCompleted ? '#fff' : 'rgba(255,255,255,0.28)',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      {stage?.label}
                    </p>
                    {isActive && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(20,184,166,0.15)', color: '#14B8A6', border: '1px solid rgba(20,184,166,0.3)' }}
                      >
                        Active
                      </span>
                    )}
                    {isCompleted && stage?.step === 4 && status === 'approved' && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}
                      >
                        Approved
                      </span>
                    )}
                    {isCompleted && stage?.step === 4 && status === 'rejected' && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                      >
                        Rejected
                      </span>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {timestamp ? (
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontFamily: 'JetBrains Mono, monospace' }}>
                        {timestamp}
                      </span>
                    ) : isPending ? (
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>~{stage?.estimatedTime}</span>
                    ) : null}
                  </div>
                </div>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.35)', lineHeight: '1.5' }}>
                  {stage?.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ApplicationTimeline;
